import React, { useState, useEffect } from "react";
import useGetTenantApplication from "../APIs/useGetTenantApplication";
import useUpdateTenantApplicationAccessLevels from "../APIs/useUpdateTenantApplicationAccessLevels";
import useGenerateAPIKey from "../APIs/useGenerateAPIKey";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "zudoku/ui/Dialog";
import { Button } from "zudoku/ui/Button";
import CopyButton from "../components/ui/copybutton";
import { useTenantStore } from "../stores/tenantStore";
import { useExposedProps } from "zudoku/hooks";
import { Card } from "zudoku/ui/Card";

export default function ApplicationAPIAccessSettings() {
  const { params, navigate } = useExposedProps();
  const { currentTenant } = useTenantStore();
  const { tenantCode, applicationId } = params;
  const { data, isFetching, error } = useGetTenantApplication(applicationId, tenantCode);
  const { mutate: updateAccessLevels, isPending } = useUpdateTenantApplicationAccessLevels(applicationId);
  const { mutate: generateAPIKey, isPending: isGeneratingAPIKey } = useGenerateAPIKey(
    applicationId,
    (key) => {
      setApiKey(key.value);
      setApiKeyExpiration(new Date(key.expiration));
      setShowAPIKeyModal(true);
    }
  );

  const [configurationAccessLevel, setConfigurationAccessLevel] = useState("None");
  const [loansAccessLevel, setLoansAccessLevel] = useState("None");
  const [drawsAccessLevel, setDrawsAccessLevel] = useState("None");

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAPIKeyModal, setShowAPIKeyModal] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [apiKeyExpiration, setApiKeyExpiration] = useState(new Date());

  useEffect(() => {
    if(currentTenant && currentTenant?.toLowerCase() !== tenantCode?.toLowerCase()){
      navigate("/applications");
    }
  }, [currentTenant, tenantCode, navigate]);

  useEffect(() => {
    if (data) {
      setConfigurationAccessLevel(data.accessLevels?.configurationAPI ?? "None");
      setLoansAccessLevel(data.accessLevels?.loansAPI ?? "None");
      setDrawsAccessLevel(data.accessLevels?.drawsAPI ?? "None");
    }
  }, [data]);

  const handleUpdateAccessLevels = () => {
    updateAccessLevels({
      configurationAPI: configurationAccessLevel,
      loansAPI: loansAccessLevel,
      drawsAPI: drawsAccessLevel,
    });
    setShowConfirmModal(false);
  };
  
  

  if (isFetching) return <AccessSettingsWrapper>Loading...</AccessSettingsWrapper>;
  if (isPending) return <AccessSettingsWrapper>Saving...</AccessSettingsWrapper>;
  if (isGeneratingAPIKey) return <AccessSettingsWrapper>Generating...</AccessSettingsWrapper>;
  if (error) return <AccessSettingsWrapper>Error: {error.message}</AccessSettingsWrapper>;

  return (
    <AccessSettingsWrapper>
      <h1 className="text-3xl font-bold mb-2">{data.name}</h1>
      <p className="text-slate-700 mb-4 dark:text-slate-400">
        {tenantCode}
      </p>
      <p className="text-slate-700 mb-4 dark:text-slate-400">
        Client ID: {data.clientId} <CopyButton text={data.clientId} />
      </p>
      <p className="text-slate-700 mb-4 dark:text-slate-400">
        *At least one API must be granted access in order to retrieve an auth token.
      </p>

      <h2 className="text-xl font-semibold mb-2">Access Levels</h2>
      <div className="space-y-4">
        <AccessLevelToggle
          label="Configuration API"
          level={configurationAccessLevel}
          onChange={setConfigurationAccessLevel}
          name="configuration"
        />
        <AccessLevelToggle
          label="Loans API"
          level={loansAccessLevel}
          onChange={setLoansAccessLevel}
          name="loans"
        />
        <AccessLevelToggle
          label="Draws API"
          level={drawsAccessLevel}
          onChange={setDrawsAccessLevel}
          name="draws"
        />
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="default" onClick={() => setShowConfirmModal(true)}>
          Save
        </Button>
        <Button
          variant="outline"
          onClick={() => generateAPIKey()}
        >
          Generate Client Secret
        </Button>
      </div>

      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Access Level Update</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to update access levels for this application?</p>
          <DialogFooter>
            <Button onClick={handleUpdateAccessLevels}>Confirm</Button>
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAPIKeyModal} onOpenChange={setShowAPIKeyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Client Secret</DialogTitle>
          </DialogHeader>
          <p>Client Secret: <code>{apiKey}</code> <CopyButton text={apiKey} /></p>
          <p>Expiration: {apiKeyExpiration.toLocaleDateString()}</p>
          <p className="text-sm text-red-700">
            Save this secret securely. It will not be shown again.
          </p>
          <DialogFooter>
            <Button onClick={() => setShowAPIKeyModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AccessSettingsWrapper>
  );
}

function AccessLevelToggle({ label, name, level, onChange }: {
  label: string,
  name: string,
  level: string,
  onChange: (val: string) => void
}) {
  return (
    <div>
      <h3 className="font-semibold mb-1">{label}</h3>
      <div className="flex space-x-4">
        {['None', 'Readonly', 'FullAccess'].map((option) => (
          <label key={option} className="inline-flex items-center space-x-2">
            <input
              type="radio"
              name={name}
              value={option}
              checked={level === option}
              onChange={() => onChange(option)}
              className="form-radio text-blue-600"
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function AccessSettingsWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full flex flex-col items-center py-6 px-6 lg:px-60 xl:px-80">
      <Card className="rounded-md w-full shadow-md p-6">
        {children}
      </Card>
    </div>
  );
}
